import type { RepoRef } from '../types/git.types'

// ── parseRepoUrl ──────────────────────────────────────────────────────────────
//
// Extracts { owner, repo } from a GitHub repo URL stored on a git Connection.
// Handles the common shapes:
//   https://github.com/owner/repo
//   https://github.com/owner/repo.git
//   https://github.com/owner/repo/         (trailing slash)
//   git@github.com:owner/repo.git          (SSH)
//   github.com/owner/repo                  (no scheme)
//
// Returns null for anything we cannot confidently parse.

export function parseRepoUrl(url: string | undefined | null): RepoRef | null {
  if (!url) return null

  const trimmed = url.trim()
  if (trimmed === '') return null

  // Normalise: drop scheme, SSH user@host prefix, then split host from path.
  // SSH form (git@github.com:owner/repo) uses ':' as the host/path separator.
  let rest = trimmed
    .replace(/^[a-z]+:\/\//i, '')   // strip https:// / http:// / git://
    .replace(/^git@/i, '')          // strip SSH user

  // SSH path separator is ':' — convert "github.com:owner/repo" → "github.com/owner/repo"
  rest = rest.replace(':', '/')

  // Strip the host segment (e.g. github.com), then the owner/repo path.
  const segments = rest.split('/').filter((s) => s.length > 0)
  // [host, owner, repo, ...]
  if (segments.length < 3) return null

  const owner = segments[1]!
  const repo = segments[2]!.replace(/\.git$/i, '')

  if (owner === '' || repo === '') return null

  return { owner, repo }
}
